import "react-datepicker/dist/react-datepicker.css";
import { useMutation, useQuery } from "@tanstack/react-query";
import * as yup from "yup";
import "react-toastify/dist/ReactToastify.css";
import PrimaryBtn, { SecondaryBtn } from "../Global/Button";
import { errorNotfiy, successNotfiy } from "../Global/Toast";
import useUserStore from "@/utils/Stores/userStore";
import { serverGET, serverPOST, serverPUT } from "@/utils/HttpFunctions";
import { DateInput } from "../Global/Input";
import TextArea from "../Global/TextArea";
import { useFormik } from "formik";
import { format } from "date-fns";
import { status } from "@/utils/constants";
import { usePrompt } from "@/utils/hooks/usePrompt";
import useTimerStore from "@/utils/Stores/timerStore";
import { useEffect, useState } from "react";
import Dropdown from "../Global/Dropdown";

const weekSchema = yup
  .object()
  .shape({
    startDate: yup
      .date()
      .nullable()
      .required("Start date is required")
      .typeError("Start date is required"),
    description: yup.string(),
    hours: yup.string(),
    minutes: yup.string(),
  })

  .required();

export default function AddTimeLog({
  isEdit,
  editedTask,
  headerInfo,
  handleClose,
  weeklyRefetch,
  isAdmin,
  isTimer,
}: {
  editedTask?: any;
  isEdit?: boolean;
  headerInfo?: any;
  handleClose?: (e?: boolean) => void;
  weeklyRefetch?: any;
  isAdmin?: boolean;
  isTimer?: boolean;
}) {
  const user = useUserStore((state: any) => state.user);
  const setTimerDescription = useTimerStore(
    (state: any) => state.setTimerDescription
  );
  const selectedProject = useTimerStore((state: any) => state.selectedProject);
  const setSelectedProject = useTimerStore(
    (state: any) => state.setSelectedProject
  );
  const [projectOptions, setProjectOptions] = useState([]);

  const { data: projectTypes } = useQuery({
    queryKey: ["projects", "timer"],
    queryFn: () => serverGET("/projects?isWeekView=1"),
    enabled: isTimer,
    staleTime: Infinity,
    gcTime: Infinity,
  });
  useEffect(() => {
    if (projectTypes) {
      const result = projectTypes?.map((p) => {
        return {
          value: p._id,
          label: p.name,
          is_active: p.is_active,
        };
      });
      setProjectOptions(result);
    }
  }, [projectTypes]);

  const { mutate, isPending } = useMutation({
    mutationFn: (task: any) => {
      return serverPOST("/timesheet/create", task);
    },
    onError: (error, variables, context: any) => {
      errorNotfiy(error?.message || "Something went wrong!");
    },
    onSuccess: async (updatedData: any, variables, context) => {
      setCustomDirty((customDirty) => !customDirty);
      if (updatedData && updatedData.constructor !== String) {
        setTimeout(() => {
          successNotfiy("Time entry has been added successfully");
        }, 2000);

        await weeklyRefetch();
        handleClose();
        return;
      }
    },
  });

  const { mutate: editMutate, isPending: updatePending } = useMutation({
    mutationFn: (task: any) => {
      return serverPUT("/timesheet/" + editedTask._id, task);
    },
    onError: (error, variables, context: any) => {
      errorNotfiy(error?.message || "Something went wrong!");
    },
    onSuccess: async (updatedData: any, variables, context) => {
      setCustomDirty((customDirty) => !customDirty);
      if (updatedData && updatedData.constructor !== String) {
        setTimeout(() => {
          successNotfiy(
            variables?.status == status.APPROVED
              ? "Time entry is approved"
              : variables?.status == status.DISAPPROVED
                ? "Time entry is disapproved"
                : "Time entry has been updated successfully"
          );
          //successNotfiy("Tasks have been updated successfully", () => {});
        }, 2000);

        await weeklyRefetch();
        handleClose();
        return;
      }
    },
  });
  const formik = useFormik({
    initialValues: {
      startDate: isTimer ? new Date() : new Date(headerInfo.taskDate),
      description: isEdit ? editedTask.description || "" : "",
      hours: isEdit
        ? Math.floor(editedTask.durationMinutes / 60).toString() || "0"
        : "",
      minutes: isEdit
        ? (editedTask.durationMinutes % 60 || 0).toString() || "0"
        : "",
    },
    validationSchema: weekSchema,
    onSubmit: async (values) => {
      if (isTimer) {
        if (!selectedProject) {
          errorNotfiy("Please select a project");
          return;
        }
        setTimerDescription(values.description);
        handleClose(true);
        return;
      }
      const body = {
        description: values.description,
        date: new Date(values.startDate).getTime(),
        durationMinutes:
          parseInt(values.hours || "0") * 60 + parseInt(values.minutes || "0"),
        project: headerInfo.project?.value,
        user: user._id,
        status:
          editedTask?.status === status.DISAPPROVED
            ? status.REVISED
            : editedTask?.status,
      };
      if (isEdit) {
        editMutate(body);
      } else {
        mutate(body);
      }
    },
  });

  const handleStatusChange = (status: string) => {
    const body = {
      status,
    };
    editMutate(body);
  };

  const { values, errors, touched, handleChange, handleSubmit, dirty } = formik;
  const { PromptModal, setCustomDirty } = usePrompt(
    "You have unsaved changes. Are you sure you want to leave?",
    dirty
  );

  return (
    <>
      <PromptModal />
      <form onSubmit={handleSubmit}>
        {isTimer ? (
          <div className="flex">
            <div className="w-full relative">
              <Dropdown
                options={projectOptions}
                value={selectedProject}
                required
                label={isTimer ? "Project Name" : ""}
                handleChange={(selected) => setSelectedProject(selected)}
                placeholder="Select Project"
              />
            </div>
          </div>
        ) : (
          <div>
            <p className="text-black-secondary font-medium text-sm mb-2">
              Project Name
            </p>
            <p className=" text-black-primary py-2">
              {editedTask
                ? editedTask.project?.name
                : headerInfo.project?.label}
            </p>
          </div>
        )}
        <div
          className={`grid  gap-4 mt-4 ${isTimer ? "grid-cols-1" : "grid-cols-2"}`}
        >
          {isAdmin ? (
            <div>
              <p className="text-xs mb-2">Date</p>
              <h1>{format(values.startDate, "MM/dd/yy")}</h1>
            </div>
          ) : (
            <DateInput
              label="Date"
              name="startDate"
              placeholder="00/00/0000"
              value={values.startDate}
              disabled={true}
              onChange={handleChange}
            />
          )}

          {!isTimer && (
            <div>
              <span className="font-normal text-sm text-black-secondary mb-2 flex">
                Duration
              </span>
              {isAdmin ? (
                <div>{`${values.hours} hours ${values.minutes} minutes`} </div>
              ) : (
                <div className="grid grid-cols-2 border text-sm border-gray-300 h-[42px] rounded-md overflow-hidden">
                  <div className="flex items-center col-span-2 ">
                    <input
                      type="text"
                      name="hours"
                      value={values.hours}
                      onChange={handleChange}
                      placeholder="Hr"
                      className="py-2 px-3 w-full border-r border-customGrey-primary focus:ring-0 focus:outline-none"
                    />
                    <input
                      type="text"
                      name="minutes"
                      value={values.minutes}
                      onChange={handleChange}
                      placeholder="Min"
                      className="p-1 px-3 w-full border-r border-customGrey-primary focus:ring-0 focus:outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="mt-4">
          {isAdmin ? (
            <div>
              <p className="text-xs mb-2">Description</p>
              <p>{values.description}</p>
            </div>
          ) : (
            <TextArea
              label="Description"
              name="description"
              placeholder="What have you worked on?"
              value={values.description}
              onChange={handleChange}
              error={errors.description?.toString()}
              touched={Boolean(touched.description)}
              height="h-36"
            />
          )}
        </div>

        <div className="flex pt-6 gap-2 w-full">
          {isAdmin ? (
            <>
              <SecondaryBtn
                text="Close"
                fullWidth={true}
                onClick={() => handleClose()}
              />
              <button
                type={"button"}
                className={`font-medium rounded-lg px-3 py-2 ${editedTask?.status === status.DISAPPROVED ? "bg-customRed-bg text-white" : "bg-customRed-light text-customRed-label"}
        hover:opacity-80 disabled:bg-gray-300 disabled:border-gray-300 text-sm w-full`}
                onClick={() => handleStatusChange(status.DISAPPROVED)}
              >
                {editedTask?.status === status.DISAPPROVED
                  ? "Rejected"
                  : "Reject"}
              </button>
              <button
                type={"button"}
                className={` font-medium rounded-lg px-3 py-2 bg-customGreen-light ${editedTask?.status === status.APPROVED ? "bg-customGreen-primary text-white" : "bg-customGreen-light text-customGreen-label"}
        hover:opacity-80 disabled:bg-gray-300 disabled:border-gray-300 text-sm w-full`}
                onClick={() => handleStatusChange(status.APPROVED)}
              >
                {editedTask?.status === status.APPROVED
                  ? "Approved"
                  : "Approve"}
              </button>
            </>
          ) : (
            <>
              <SecondaryBtn
                text="Cancel"
                fullWidth={true}
                onClick={() => handleClose(false)}
              />
              <PrimaryBtn
                text={isTimer ? "Start" : "Save"}
                type="submit"
                loading={isPending || updatePending}
                disabled={
                  isPending ||
                  updatePending ||
                  editedTask?.status === status.APPROVED
                }
                fullWidth={true}
                // onClick={handleConfirmClick}
              />
            </>
          )}
        </div>
      </form>
    </>
  );
}
